<template>
  <component :is="layoutComponent" v-if="layoutComponent">
    <router-view />
  </component>
</template>

<script lang="ts" setup>
import LayoutAdminDashboard from '@/layouts/LayoutAdminDashboard.vue';
import LayoutBlank from '@/layouts/LayoutBlank.vue';
import { useSplitStore } from '@/stores/split/useSplitStore';
import { captureError } from '@smg-real-estate/browser-rum';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  getFeatureFlags,
  SplitSupportedEnvironment,
  SplitSupportedPlatform,
} from '@smg-real-estate/vue-experiments';
import { useHead } from '@unhead/vue';
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { initialLoadTracker } from './helpers/Monitoring/DataDogMonitor';
import { UBMFlagSet } from './helpers/Split';

const { locale } = useI18n();
const lang = computed(() =>
  locale.value === HgSupportedLanguage.EN ? locale.value : `${locale.value}-CH`,
);

useHead({
  htmlAttrs: {
    lang,
  },
});

const route = useRoute();

const layoutComponent = computed(() => {
  if (!route.name) {
    initialLoadTracker.start();
    return null;
  }

  initialLoadTracker.end();

  if (route.meta?.hasBlankLayout) {
    return LayoutBlank;
  }

  return LayoutAdminDashboard;
});

const splitStore = useSplitStore();

onMounted(async () => {
  try {
    const featureFlagsResponse = await getFeatureFlags([UBMFlagSet], {
      platform: SplitSupportedPlatform.SMG,
      // Map any non-prod stage (personal, dev, test) to the test experiments endpoint.
      // VUE_APP_STAGE values like 'personal' are not valid SplitSupportedEnvironment keys,
      // which would produce an undefined base URL and a broken relative request URL.
      env:
        import.meta.env.VUE_APP_STAGE === 'prod'
          ? SplitSupportedEnvironment.PROD
          : SplitSupportedEnvironment.TEST,
    });
    const { userKey, treatmentsWithConfig } = featureFlagsResponse.data.data;
    splitStore.updateSplitStoreFromApi({
      splitSessionKey: userKey,
      splitTreatmentsWithConfig: treatmentsWithConfig,
    });
  } catch (error) {
    captureError(error);
  }
});
</script>

<style lang="scss">
@import './assets/global-smg';
</style>
